/**
 * Starts one host VM, named, for the host pages: every place that tells an
 * operator "the prepare host is offline" should be a place they can do something
 * about it. The release flow starts both machines at once instead (`Wake`), and
 * both go through `lib/hostPower.ts` so the error reporting cannot drift.
 *
 * On a host that offers machine types this is a split button: Start keeps the
 * shape the VM is already on, and the menu beside it is how an operator gets past
 * a stockout — or back onto the usual shape afterwards, since Pulumi no longer
 * reverts it. See `MachineTypeMenu`.
 */
import { useState } from 'react';
import { Stack, Tooltip } from '@mui/material';
import { hostInstance, unprovisionedHostMessage, type HostRole } from '@finvari/deployer';
import { startHostOn, startHostReportingError, startHostTryingAll, type StartFailure } from '../lib/hostPower';
import { StartFailureAlert, StartHostControl, type MachineTypeRequest } from './MachineTypeMenu';

export interface StartHostButtonProps {
    role: HostRole;
    /** Button face; defaults to a bare "Start". */
    label?: string;
    size?: 'small' | 'medium';
    /** The shape the VM is on, from its heartbeat, so Start can say what it keeps. */
    currentMachineType?: string;
}

export function StartHostButton({ role, label = 'Start', size, currentMachineType }: StartHostButtonProps) {
    const [sending, setSending] = useState(false);
    const [failure, setFailure] = useState<StartFailure | undefined>(undefined);
    const unprovisioned = !hostInstance(role);

    const start = async (request?: MachineTypeRequest) => {
        setSending(true);
        setFailure(undefined);
        setFailure(
            request === undefined
                ? await startHostReportingError(role)
                : request.kind === 'all'
                  ? await startHostTryingAll(role)
                  : await startHostOn(role, request.machineType),
        );
        setSending(false);
    };

    const control = (
        <StartHostControl
            role={role}
            onStart={(request) => void start(request)}
            sending={sending}
            disabled={unprovisioned}
            label={label}
            currentMachineType={currentMachineType}
            size={size}
        />
    );

    return (
        <Stack spacing={1} alignItems="flex-start">
            {unprovisioned ? (
                <Tooltip title={unprovisionedHostMessage(role)}>
                    <span>{control}</span>
                </Tooltip>
            ) : (
                control
            )}
            {failure && (
                <StartFailureAlert
                    role={role}
                    failure={failure}
                    title="The host did not start"
                    onChoose={(request) => void start(request)}
                    sending={sending}
                />
            )}
        </Stack>
    );
}
